import React, { useState, useEffect } from "react";
import "./Realisations.css";
import {
	AiOutlineLink,
	AiOutlineCalendar,
	AiOutlineFolder,
	AiOutlineEye,
	AiOutlineClose
} from "react-icons/ai";
import { BsChevronDown, BsChevronUp } from "react-icons/bs";
import Filter from "./Filter";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from 'react-i18next';
import SEO from '../seo/SEO';

export default function Realisations() {
	const { t } = useTranslation();
	const [realisation, setRealisation] = useState([]);
	const [realisationFilter, setRealisationFilter] = useState([]);
	const [activeCategory, setActiveCategory] = useState(0);
	const [expandedItem, setExpandedItem] = useState(null);
	const [itemsPerRow, setItemsPerRow] = useState(3);

	// Calculer le nombre d'items par rangée selon la largeur d'écran
	useEffect(() => {
		const updateItemsPerRow = () => {
			const width = window.innerWidth;
			if (width <= 575) setItemsPerRow(1);
			else if (width <= 768) setItemsPerRow(2);
			else setItemsPerRow(3);
		};

		updateItemsPerRow();
		window.addEventListener('resize', updateItemsPerRow);
		return () => window.removeEventListener('resize', updateItemsPerRow);
	}, []);

	useEffect(() => {
		fetchRealisations();
	}, []);

	const fetchRealisations = () => {
		// Charger les réalisations depuis localStorage (géré par le dashboard admin)
		const savedPortfolios = localStorage.getItem('portfolios');
		if (savedPortfolios) {
			const portfolios = JSON.parse(savedPortfolios);
			setRealisation(portfolios);
			setRealisationFilter(portfolios);
		} else {
			// Données par défaut si aucune donnée n'existe
			const defaultPortfolios = [
				{
					id: 1,
					name: "Site E-commerce",
					description: "Développement d'une boutique en ligne moderne avec interface utilisateur moderne, panier d'achat, système de paiement intégré et tableau de bord administrateur.",
					url: "https://example.com",
					category: "siteweb",
					date: "2023",
					image: "/images/realisation-1.png",
					gallery: [
						"/images/realisation-1.png",
						"/images/realisation-2.png"
					],
					technologies: ["React", "Node.js", "MongoDB", "Stripe"],
					client: "E-Commerce Store"
				},
				{
					id: 2,
					name: "Identité Visuelle Restaurant",
					description: "Création complète d'une identité visuelle pour un restaurant gastronomique incluant logo, charte graphique, menu design et supports de communication.",
					url: "https://example.com",
					category: "identitevisuelle",
					date: "2023",
					image: "/images/realisation-2.png",
					gallery: [
						"/images/realisation-2.png",
						"/images/realisation-1.png"
					],
					technologies: ["Photoshop", "Illustrator", "InDesign"],
					client: "Le Gourmet"
				}
			];
			setRealisation(defaultPortfolios);
			setRealisationFilter(defaultPortfolios);
			localStorage.setItem('portfolios', JSON.stringify(defaultPortfolios));
		}
	};

	return (
		<section id="realisations" className="content_section">
			<SEO 
				page="portfolio" 
				title={t('portfolio.title')} 
				description={`Découvrez mes ${realisation.length} réalisations en développement web et design graphique`}
				portfolioItems={realisation}
			/>
			<div className="content_section_inner">
				<h2>{t('portfolio.title')}</h2>
				{/* MENU TABS */}
				<Filter
					realisation={realisation}
					setFiltered={setRealisationFilter}
					activeCategory={activeCategory}
					setActiveCategory={setActiveCategory}
				/>

				{/* GALLERY */}
				{realisationFilter?.length === 0 ? (
					<p className="realisation-error">
						{t('portfolio.noResults')}
					</p>
				) : (
					<motion.div layout className="realisation-grid">
						<AnimatePresence>
							{(() => {
								const items = [];
								let expandedRowIndex = -1;
								
								// Si un item est expanded, calculer sa rangée
								if (expandedItem !== null) {
									const expandedIndex = realisationFilter.findIndex((elem, i) => (elem.id || i) === expandedItem);
									expandedRowIndex = Math.floor(expandedIndex / itemsPerRow);
								}

								realisationFilter?.forEach((elem, i) => {
									const currentRowIndex = Math.floor(i / itemsPerRow);
									const itemId = elem.id || i;
									
									// Ajouter l'item
									items.push(
										<motion.div
											key={`card-${itemId}`}
											className="realisation-card"
											layout
											animate={{ opacity: 1 }}
											initial={{ opacity: 0 }}
											exit={{ opacity: 0 }}
										>
											<div className="realisation-item">
												<img
													className="realisation-image"
													src={elem.image}
													alt={elem.name}
													width="100%"
												/>

												<div className="realisation-overlay">
													<div className="overlay-top">
														<h4>{elem.name}</h4>
														<p>{elem.description?.substring(0, 60)}...</p>
													</div>
													<div className="overlay-middle">
														{elem.url && (
															<a
																href={elem.url}
																target="_blank"
																rel="noreferrer"
																title="Voir le projet"
															>
																<AiOutlineLink />
															</a>
														)}
														<button
															onClick={(e) => {
																e.preventDefault();
																e.stopPropagation();
																setExpandedItem(expandedItem === itemId ? null : itemId);
															}}
															title="Voir détails"
														>
															{expandedItem === itemId ? <BsChevronUp /> : <AiOutlineEye />}
														</button>
													</div>
													<div className="overlay-bottom">
														<p>
															<AiOutlineCalendar /> {elem.date}
														</p>
														<p> 
															<AiOutlineFolder /> {elem.category}
														</p>
													</div>
												</div>
											</div>
										</motion.div>
									);

									// Si c'est le dernier item de la rangée ET qu'il y a un item expanded dans cette rangée
									const isLastInRow = (i + 1) % itemsPerRow === 0 || i === realisationFilter.length - 1;
									if (isLastInRow && expandedRowIndex === currentRowIndex && expandedItem !== null) {
										const expandedData = realisationFilter.find((elem, idx) => (elem.id || idx) === expandedItem);
										
										items.push(
											<motion.div
												key={`expanded-${expandedItem}`}
												className="expanded-details"
												initial={{ opacity: 0, height: 0 }}
												animate={{ opacity: 1, height: 'auto' }}
												exit={{ opacity: 0, height: 0 }}
												transition={{ duration: 0.3 }}
											>
												<div className="expanded-content">
													<button 
														className="close-expanded"
														onClick={() => setExpandedItem(null)}
													>
														<AiOutlineClose />
													</button>

													<div className="expanded-grid">
														<div className="expanded-left">
															<div className="main-image">
																<img src={expandedData.image} alt={expandedData.name} />
															</div>
															
															{expandedData.gallery && expandedData.gallery.length > 1 && (
																<div className="gallery-grid">
																	<h4>Images</h4>
																	<div className="gallery-images">
																		{expandedData.gallery.filter(img => img.trim()).slice(0, 6).map((img, index) => (
																			<img 
																				key={index} 
																				src={img} 
																				alt={`${expandedData.name} ${index + 1}`}
																				className="gallery-thumb"
																			/>
																		))}
																	</div>
																</div>
															)}
														</div>

														<div className="expanded-right">
															<div className="project-header">
																<h3>{expandedData.name}</h3>
																{expandedData.client && (
																	<p className="client-name">Client: {expandedData.client}</p>
																)}
															</div>

															<div className="project-meta">
																<div className="meta-item">
																	<AiOutlineCalendar />
																	<span>{expandedData.date}</span>
																</div>
																<div className="meta-item">
																	<AiOutlineFolder />
																	<span>{expandedData.category}</span>
																</div>
															</div>

															<div className="project-description">
																<p>{expandedData.description}</p>
															</div>

															{expandedData.technologies && expandedData.technologies.length > 0 && (
																<div className="project-technologies">
																	<h4>Technologies</h4>
																	<div className="tech-tags">
																		{expandedData.technologies.filter(tech => tech.trim()).map((tech, index) => (
																			<span key={index} className="tech-tag">
																				{tech}
																			</span>
																		))}
																	</div>
																</div>
															)}

															{expandedData.url && (
																<div className="project-actions">
																	<a 
																		href={expandedData.url} 
																		target="_blank" 
																		rel="noreferrer"
																		className="project-link-btn"
																	>
																		<AiOutlineLink />
																		Voir le projet
																	</a>
																</div>
															)}
														</div>
													</div>
												</div>
											</motion.div>
										);
									}
								});

								return items;
							})()}
						</AnimatePresence>
					</motion.div>
				)}
			</div>
		</section>
	);
}
